import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StoreService } from "@/gen/omnios/v1/store_connect";
import { TokenService } from "@/gen/omnios/v1/token_connect";
import type { SearchKey } from "@/gen/omnios/v1/token_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Link,
	Outlet,
	createFileRoute,
	useLocation,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import {
	BarChart3,
	Check,
	Code,
	FileCode,
	FileText,
	Globe,
	Key,
	Pencil,
	Rocket,
	Search,
	Settings2,
	Sparkles,
	Trash2,
	X,
} from "lucide-react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/portals/$id")({
	component: PortalDetailLayout,
});

const storeClient = createClient(StoreService, transport);
const tokenClient = createClient(TokenService, transport);

// ── Context ──

export interface PortalDetailCtx {
	portal: {
		id: string;
		name: string;
		slug: string;
		status: string;
		activeSessionId: string;
		storeUrl: string;
		templateId: string;
		previewUrl: string;
		publishedAt?: { seconds: bigint };
		updatedAt?: { seconds: bigint };
		portalTypeId?: string;
		portalTypeConfig?: Record<string, unknown>;
	} | null;
	keys: SearchKey[];
	loading: boolean;
	reload: () => Promise<void>;
	portalApiUrl: string;
}

const PortalDetailContext = createContext<PortalDetailCtx>({
	portal: null,
	keys: [],
	loading: true,
	reload: async () => {},
	portalApiUrl: "",
});

export function usePortalDetail(): PortalDetailCtx {
	return useContext(PortalDetailContext);
}

// ── Nav ──

interface NavItem {
	to: string;
	label: string;
	icon: typeof Globe;
}

function getNavItems(id: string): NavItem[] {
	return [
		{ to: `/portals/${id}/overview`, label: "nav_overview", icon: Globe },
		{ to: `/portals/${id}/build`, label: "nav_editor", icon: Code },
		{ to: `/portals/${id}/config`, label: "nav_settings", icon: Settings2 },
		{ to: `/portals/${id}/search`, label: "nav_search", icon: Search },
		{ to: `/portals/${id}/keys`, label: "nav_keys", icon: Key },
		{
			to: `/portals/${id}/analytics`,
			label: "nav_analytics",
			icon: BarChart3,
		},
		{
			to: `/portals/${id}/recommendations`,
			label: "nav_recommendations",
			icon: Sparkles,
		},
		{ to: `/portals/${id}/policies`, label: "nav_policies", icon: FileText },
		{ to: `/portals/${id}/api`, label: "nav_api", icon: FileCode },
	]
}

const STATUS_DOT: Record<string, string> = {
	draft: "bg-zinc-400",
	building: "bg-amber-500 animate-pulse",
	preview: "bg-blue-500",
	published: "bg-emerald-500",
	archived: "bg-zinc-400",
};

const STATUS_LABELS: Record<string, string> = {
	draft: "status_draft",
	building: "status_building",
	preview: "status_preview",
	published: "status_published",
	archived: "status_archived",
};

// ── Layout ──

function PortalDetailLayout() {
	const { id } = useParams({ from: "/portals/$id" });
	const location = useLocation();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const { t } = useTranslation("portals");

	const { data: portal, isLoading: sfLoading } = useQuery({
		queryKey: ["portal", id],
		queryFn: () => storeClient.getPortalInstance({ id }),
	})

	const [keys, setKeys] = useState<SearchKey[]>([]);
	const [keysLoading, setKeysLoading] = useState(true);

	const portalApiUrl =
		import.meta.env.VITE_PORTAL_API_URL ?? "http://localhost:50101";

	const reload = useCallback(async () => {
		setKeysLoading(true);
		try {
			const keysRes = await tokenClient.listSearchKeys({});
			setKeys(
				keysRes.keys.filter(
					(k: any) => !k.portalInstanceId || k.portalInstanceId === id,
				),
			)
		} catch {
			setKeys([]);
		} finally {
			setKeysLoading(false);
		}
		qc.invalidateQueries({ queryKey: ["portal", id] });
	}, [id, qc]);

	useEffect(() => {
		reload();
	}, [reload]);

	useBreadcrumbs([
		{ label: t("breadcrumb_portals"), href: "/portals" },
		{ label: portal?.name ?? "..." },
	])

	useEffect(() => {
		if (location.pathname === `/portals/${id}`) {
			navigate({ to: `/portals/${id}/overview`, replace: true });
		}
	}, [location.pathname, id, navigate]);

	const navItems = useMemo(() => getNavItems(id), [id]);

	// ── Inline rename ──
	const [editing, setEditing] = useState(false);
	const [editName, setEditName] = useState("");

	const renameMutation = useMutation({
		mutationFn: (newName: string) =>
			storeClient.updatePortalInstance({ id, name: newName }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["portal", id] });
			qc.invalidateQueries({ queryKey: ["portals"] });
			setEditing(false);
			toast.success(t("name_updated"));
		},
	})

	// ── Delete ──
	const [showDelete, setShowDelete] = useState(false);

	const deleteMutation = useMutation({
		mutationFn: () => storeClient.deletePortalInstance({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["portals"] });
			toast.success(t("portal_deleted"));
			navigate({ to: "/portals" });
		},
	})

	// ── Publish ──
	const publishMutation = useMutation({
		mutationFn: () => storeClient.publishPortalInstance({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["portal", id] });
			toast.success(t("portal_published"));
		},
	})

	const ctx = useMemo<PortalDetailCtx>(
		() => ({
			portal: portal
				? {
						id: portal.id,
						name: portal.name,
						slug: portal.slug,
						status: portal.status,
						activeSessionId: portal.activeSessionId,
						storeUrl: portal.storeUrl,
						templateId: portal.templateId,
						previewUrl: portal.previewUrl,
						publishedAt: portal.publishedAt,
						updatedAt: portal.updatedAt,
						portalTypeId: portal.portalTypeId,
						portalTypeConfig: portal.portalTypeConfig?.toJson() as Record<string, unknown> | undefined,
					}
				: null,
			keys,
			loading: sfLoading || keysLoading,
			reload,
			portalApiUrl,
		}),
		[portal, keys, sfLoading, keysLoading, reload, portalApiUrl],
	)

	const status = portal?.status ?? "draft";
	const canPublish =
		status !== "published" && status !== "archived" && status !== "draft";

	return (
		<PortalDetailContext.Provider value={ctx}>
			<div
				className="flex h-full overflow-hidden"
			>
				{/* Sub-sidebar */}
				<aside className="w-56 shrink-0 border-r bg-muted/30 overflow-y-auto flex flex-col">
					{/* Header with inline rename */}
					<div className="p-3 border-b space-y-2">
						{editing ? (
							<div className="flex items-center gap-1">
								<Input
									value={editName}
									onChange={(e) => setEditName(e.target.value)}
									className="h-7 text-sm"
									autoFocus
									onKeyDown={(e) => {
										if (e.key === "Enter" && editName.trim())
											renameMutation.mutate(editName.trim());
										if (e.key === "Escape") setEditing(false);
									}}
								/>
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7 shrink-0"
									onClick={() => {
										if (editName.trim()) renameMutation.mutate(editName.trim());
									}}
								>
									<Check className="h-3.5 w-3.5" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7 shrink-0"
									onClick={() => setEditing(false)}
								>
									<X className="h-3.5 w-3.5" />
								</Button>
							</div>
						) : (
							<div className="flex items-center gap-1.5 group/name">
								<h2 className="text-sm font-semibold truncate flex-1">
									{portal?.name ?? "..."}
								</h2>
								<Button
									variant="ghost"
									size="icon"
									className="h-5 w-5 opacity-0 group-hover/name:opacity-100 text-muted-foreground hover:text-foreground"
									onClick={() => {
										setEditName(portal?.name ?? "");
										setEditing(true)
									}}
								>
									<Pencil className="h-3 w-3" />
								</Button>
							</div>
						)}
						<div className="flex items-center gap-2">
							<span
								className={cn(
									"h-2 w-2 rounded-full shrink-0",
									STATUS_DOT[status],
								)}
							/>
							<span className="text-xs text-muted-foreground">
								{t(STATUS_LABELS[status])}
							</span>
							{portal?.portalTypeId && portal.portalTypeId !== "storefront" && (
								<span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">
									{portal.portalTypeId}
								</span>
							)}
							{portal?.slug && (
								<span className="text-xs text-muted-foreground/50 truncate">
									/{portal.slug}
								</span>
							)}
						</div>
					</div>

					{/* Nav */}
					<nav className="flex-1 p-2 space-y-0.5">
						{navItems.map((item) => {
							const isActive = location.pathname.startsWith(item.to);
							return (
								<Link
									key={item.to}
									to={item.to}
									className={cn(
										"flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium",
										"text-muted-foreground transition-colors",
										"hover:bg-accent hover:text-accent-foreground",
										isActive && "bg-accent text-accent-foreground",
									)}
								>
									<item.icon className="h-4 w-4 shrink-0" />
									{t(item.label)}
								</Link>
							)
						})}
					</nav>

					{/* Footer actions */}
					<div className="p-2 space-y-1 border-t">
						{canPublish && (
							<Button
								size="sm"
								className="w-full gap-1.5"
								onClick={() => publishMutation.mutate()}
								disabled={publishMutation.isPending}
							>
								<Rocket className="h-3.5 w-3.5" />
								{publishMutation.isPending ? t("publishing") : t("publish")}
							</Button>
						)}
						<Button
							variant="ghost"
							size="sm"
							className="w-full gap-1.5 text-muted-foreground hover:text-destructive"
							onClick={() => setShowDelete(true)}
						>
							<Trash2 className="h-3.5 w-3.5" />
							{t("delete")}
						</Button>
					</div>
				</aside>

				{/* Content */}
				<div className="flex-1 overflow-y-auto p-6">
					<Outlet />
				</div>
			</div>

			{/* Delete confirmation */}
			<AlertDialog open={showDelete} onOpenChange={setShowDelete}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("delete_confirm_title", { name: portal?.name })}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("delete_confirm_desc")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteMutation.mutate()}
							className="bg-destructive text-destructive-foreground"
						>
							{t("delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PortalDetailContext.Provider>
	)
}
